import { styled } from '@mui/material/styles'
import { Box, Typography, Paper, IconButton, Button } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AddIcon from '@mui/icons-material/Add'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'

export const SlidingBarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isHidden',
})<{ $isHidden: boolean }>(({ $isHidden }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: $isHidden ? '0px' : 'auto',
  minWidth: $isHidden ? '0px' : 'auto',
  overflow: 'hidden',
  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
}))

export const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: 'none',
  width: 200,
  [theme.breakpoints.down('sm')]: {
    width: 180,
  },
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  boxShadow: '2px 0 12px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: '3px 0 16px rgba(0,0,0,0.15)',
  },
  isolation: 'isolate',
}))

export const MinimizedTab = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 20,
  left: 20,
  padding: theme.spacing(1.5),
  borderRadius: 12,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 1000,
  minWidth: 180,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
  },
}))

export const StyledChip = styled(Box)(({ theme }) => ({
  background: theme.palette.primary.light,
  color: theme.palette.primary.dark,
  fontWeight: 600,
  fontSize: 11,
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease-in-out',
  padding: '2px 10px',
  width: 'unset',
}))

export const ScrollContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isEmpty',
})<{ $isEmpty?: boolean }>(({ theme, $isEmpty = false }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  flex: 1,
  isolation: 'isolate',
  overflowY: 'auto',
  overflowX: 'hidden',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.1)',
    borderRadius: '3px',
    '&:hover': {
      background: 'rgba(0,0,0,0.15)',
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(0,0,0,0.1) transparent',
  '&::-webkit-scrollbar-thumb:vertical': {
    minHeight: '30px',
    background: 'rgba(0,0,0,0.1)',
  },
  '&::-webkit-scrollbar-thumb:vertical:inactive': {
    background: 'rgba(0,0,0,0.1)',
  },
  ...($isEmpty && {
    justifyContent: 'center',
  }),
}))

export const StyledTitleBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  alignSelf: 'stretch',
}))

export const StyledMinimizeButton = styled(IconButton)(({ theme }) => ({
  background: theme.palette.primary.main,
  borderRadius: '50%',
  height: '28px',
  width: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'primary',
  opacity: 0.7,
  textTransform: 'none',
  fontWeight: 600,
  padding: '6px 16px',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: 'none',
    background: theme.palette.primary.dark,
  },
}))

export const CheckInButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  opacity: 0.7,
  textTransform: 'none',
  fontWeight: 600,
  padding: '6px 16px',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: 'none',
    background: theme.palette.primary.dark,
    transform: 'translateY(-1px)',
  },
}))

export const StyledAverage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: '0.75rem',
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}))

export const StyledTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  letterSpacing: '-0.02em',
  fontSize: '1rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
  },
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
}))

export const StyledEmptyWaitingRoom = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  color: theme.palette.text.secondary,
  textAlign: 'center',
  fontSize: 14,
  fontWeight: 500,
  borderRadius: 12,
  border: `1px dashed ${theme.palette.grey[300]}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '05',
    color: theme.palette.primary.main,
  },
}))

export const StyledArrowLeftIcon = styled(ChevronLeftIcon)(() => ({
  fontSize: 20,
  color: 'white',
}))

export const StyledAccessTimeIcon = styled(AccessTimeIcon)(() => ({
  fontSize: 12,
  opacity: 0.7,
}))

export const StyledLargeAddIcon = styled(AddIcon)(() => ({
  fontSize: 30,
}))

export const WaitingRoomHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  width: '100%',
}))
